import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	input,
	ViewEncapsulation,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import { TranslatePipe } from '@ngx-translate/core';

import { AlertComponent } from '@valk-nx/components/ui-alert/src/lib/alert.component';
import { PersuadeCardComponent } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.component';
import { LANGUAGE, PRICING_TOOLTIP_OCCUPANCY } from '@valk-nx/core/lib/core';
import { BOOKINGTOOL } from '@valk-nx/storyblok-services/src/lib/globals';
import { FilterableListService } from '@valk-nx/storyblok-services/src/lib/services/filterable-list.service';
import { HOST } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import type { ContentData } from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type {
	FilterableHotel,
	TransformedFilterableHotel,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';

import { sortByOption } from '../../filterable-list.helpers';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
	host: { class: 'contents' },
	selector: 'sb-filterable-list-vdc-hotels',
	templateUrl: './vdc-hotels.html',
	imports: [
		AlertComponent,
		PersuadeCardComponent,
		RouterModule,
		TranslatePipe,
	],
})
export class VdcHotels {
	private readonly filterableListService = inject(FilterableListService);
	private readonly bookingtoolUrl = inject(BOOKINGTOOL);
	private readonly host = inject(HOST);
	private readonly defaultLanguage = inject(LANGUAGE);

	sortOption = input.required<string>();
	baseUrl = input.required<string>();
	showPrices = input<boolean>(true);
	business = input.required<boolean>();

	readonly pricingTooltipOccupancy = PRICING_TOOLTIP_OCCUPANCY;

	hotels = computed<TransformedFilterableHotel[]>(
		() =>
			this.filterableListService.vdcHotels
				.value()
				?.hotels.map((hotel) => ({
					...hotel,
					content: this.transformContent(hotel.content),
					toUrl: this.buildToUrl(hotel),
				})) ?? [],
	);

	sortedHotels = computed<TransformedFilterableHotel[]>(() =>
		sortByOption(this.hotels(), this.sortOption()),
	);

	private readonly buildToUrl = (
		hotel: FilterableHotel,
	): string | undefined => {
		if (this.business()) {
			return this.buildHotelPageUrl(hotel.slug);
		}

		if (hotel.availability.isBookable) {
			return `${this.bookingtoolUrl}?hotelSlug=${hotel.slug}`;
		}

		return undefined;
	};

	private readonly buildHotelPageUrl = (slug: string): string => {
		const language = this.filterableListService.language();
		const languagePrefix =
			language === this.defaultLanguage ? '' : `/${language}`;

		return `${this.host}${languagePrefix}/hotels/${slug}`;
	};

	private readonly transformContent = (content: ContentData): string => {
		if (content?.type === 'array') {
			return content.value.map((item) => `- ${item}`).join('\n');
		}
		return content.value as string;
	};
}
